import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import config from "../../../configs/config";
import { createAgency } from "../../../entities/agency/model/agencySlice";
import { selectAgencyState } from "../../../entities/agency/model/selectors";
import { getCurrentLanguageKey, getLanguageSection } from "../../../shared/services/languageService";
import { parseJsonResponse as parseApiJsonResponse } from "../../../shared/api/httpClient";

const createInitialFormState = () => ({
  Id_Country: "",
  AgencyType: "1",
  AgencyName: "",
  Phone1: "",
  Address: "",
  Address_2: "",
  ZipCode: "",
  City: "",
  Id_State: "",
  ContactName: "",
  ContactLastName: "",
  ContactTitle: "",
  Phone2: "",
  Email: "",
  password: "",
  passwordv: "",
  terms: false,
  policies: false,
  mobileAppName: "SunExcursions",
  isBranchAgency: false,
  hasHQ: true,
  RegistrationType: "Single",
  marivalrewards: "1"
});

const inputClass = "mr-login-input h-11 w-full rounded-[10px] border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-[#11183f] shadow-none";
const textareaClass = "mr-login-input min-h-[78px] w-full resize-y rounded-[10px] border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-[#11183f] shadow-none";
const labelClass = "mb-2 block text-xs font-black uppercase tracking-wide text-slate-500";
const sectionClass = "rounded-xl border border-slate-200 bg-slate-50 p-5";

// Definidos a nivel de módulo a propósito: si viven dentro de AgencyCreate, cada
// render crea un tipo de componente nuevo y React remonta los inputs (se pierde el foco).
const Field = ({ className = "", label, children }) => (
  <div className={className}>
    <label className={labelClass}>{label}</label>
    {children}
  </div>
);

const Section = ({ title: sectionTitle, help, children }) => (
  <section className={sectionClass}>
    <div className="mb-4">
      <h3 className="m-0 text-base font-black text-[#11183f]">{sectionTitle}</h3>
      <p className="m-0 mt-1 text-sm leading-6 text-slate-500">{help}</p>
    </div>
    <div className="grid gap-4 md:grid-cols-12">
      {children}
    </div>
  </section>
);

function AgencyCreate({
  withButton = true,
  buttonText = "Crear agencia",
  title = "Registrar agencia",
  showInlineTitle = true
}) {
  const language = getLanguageSection("agencyregister");
  const createLanguage = language?.create || {};
  const currentLanguage = getCurrentLanguageKey();
  const multimediaTermsLink =
    currentLanguage === "esp"
      ? "https://marivalagents.com/es/terminos-y-condiciones-multimedia"
      : "https://marivalagents.com/multimedia-terms-and-conditions";
  const policyLinks = {
    cookie: currentLanguage === "esp" ? "/es/politica-de-cookies" : "/cookie-policy",
    privacy: currentLanguage === "esp" ? "/es/aviso-de-privacidad" : "/privacy-notice"
  };
  const dispatch = useDispatch();
  const { creating, createError } = useSelector(selectAgencyState);

  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [showInlineSuccess, setShowInlineSuccess] = useState(false);
  const [form, setForm] = useState(createInitialFormState);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadCountries = async () => {
      setLoadingCountries(true);

      try {
        const response = await fetch(`${config.apiurl}GetCountries`, {
          method: "GET",
        });

        if (!response.ok) {
          throw new Error(createLanguage.countryLoadError || "No se pudieron cargar los países.");
        }

        const json = await parseApiJsonResponse(response);
        const nextCountries = Array.isArray(json?.data) ? json.data : [];

        if (!isMounted) return;
        setCountries(nextCountries);
      } catch (loadError) {
        if (!isMounted) return;
        setCountries([]);
        setError(loadError.message || createLanguage.countryLoadError || "No se pudieron cargar los países.");
      } finally {
        if (isMounted) {
          setLoadingCountries(false);
        }
      }
    };

    loadCountries();

    return () => {
      isMounted = false;
    };
  }, [createLanguage.countryLoadError]);

  useEffect(() => {
    let isMounted = true;

    const loadStates = async () => {
      if (!form.Id_Country) {
        setStates([]);
        return;
      }

      setLoadingStates(true);

      try {
        const response = await fetch(`${config.apiurl}GetStates?Id_Country=${encodeURIComponent(form.Id_Country)}`, {
          method: "GET",
        });

        if (!response.ok) {
          throw new Error(createLanguage.stateLoadError || "No se pudieron cargar los estados.");
        }

        const json = await parseApiJsonResponse(response);
        const nextStates = Array.isArray(json?.data) ? json.data : [];

        if (!isMounted) return;
        setStates(nextStates);
      } catch (loadError) {
        if (!isMounted) return;
        setStates([]);
        setError(loadError.message || createLanguage.stateLoadError || "No se pudieron cargar los estados.");
      } finally {
        if (isMounted) {
          setLoadingStates(false);
        }
      }
    };

    loadStates();

    return () => {
      isMounted = false;
    };
  }, [createLanguage.stateLoadError, form.Id_Country]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    document.body.classList.toggle("mr-agency-modal-open", showModal);

    return () => {
      document.body.classList.remove("mr-agency-modal-open");
    };
  }, [showModal]);

  const openModal = () => {
    setError("");
    setShowInlineSuccess(false);
    setShowModal(true);
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const closeModal = () => {
    setError("");
    setShowModal(false);
  };

  const resetForm = () => {
    setForm(createInitialFormState());
    setStates([]);
    setError("");
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => {
      const nextValue = type === "checkbox" ? checked : value;

      if (name === "Id_Country") {
        return {
          ...prev,
          Id_Country: nextValue,
          Id_State: ""
        };
      }

      return {
        ...prev,
        [name]: nextValue
      };
    });

    if (error) setError("");
  };

  const validateForm = () => {
    if (!form.AgencyName.trim()) return createLanguage.agencyNameRequired || "El nombre de la agencia es obligatorio.";
    if (!form.Address.trim()) return createLanguage.addressRequired || "La dirección es obligatoria.";
    if (!form.Phone1.trim()) return createLanguage.primaryPhoneRequired || "El teléfono principal es obligatorio.";
    if (!form.City.trim()) return createLanguage.cityRequired || "La ciudad es obligatoria.";
    if (!form.Id_Country) return createLanguage.countryRequired || "El país es obligatorio.";
    if (!form.Id_State) return createLanguage.stateRequired || "El estado es obligatorio.";
    if (!form.ContactName.trim()) return createLanguage.contactNameRequired || "El nombre del contacto es obligatorio.";
    if (!form.ContactLastName.trim()) return createLanguage.contactLastNameRequired || "El apellido del contacto es obligatorio.";
    if (!form.Email.trim()) return createLanguage.emailRequired || "El email es obligatorio.";
    if (!form.password.trim()) return createLanguage.passwordRequired || "La contraseña es obligatoria.";
    if (form.password !== form.passwordv) return createLanguage.passwordMismatch || "Las contraseñas no coinciden.";
    if (!form.terms) return createLanguage.termsRequired || "Debes aceptar los términos.";
    if (!form.policies) return createLanguage.policiesRequired || "Debes aceptar el aviso de privacidad y la política de cookies.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      Id_Country: form.Id_Country,
      AgencyType: form.AgencyType,
      AgencyName: form.AgencyName.trim(),
      Phone1: form.Phone1.trim(),
      Address: form.Address.trim(),
      Address_2: form.Address_2.trim(),
      ZipCode: form.ZipCode.trim(),
      City: form.City.trim(),
      Id_State: form.Id_State,
      ContactName: form.ContactName.trim(),
      ContactLastName: form.ContactLastName.trim(),
      ContactTitle: form.ContactTitle.trim(),
      Phone2: form.Phone2.trim(),
      Email: form.Email.trim(),
      password: form.password,
      passwordv: form.passwordv,
      terms: form.terms ? "on" : "",
      mobileAppName: form.mobileAppName,
      isBranchAgency: form.isBranchAgency ? "true" : "false",
      hasHQ: form.hasHQ ? "true" : "false",
      RegistrationType: form.RegistrationType,
      marivalrewards: form.marivalrewards
    };

    const resultAction = await dispatch(createAgency(payload));

    if (createAgency.fulfilled.match(resultAction)) {
      resetForm();
      if (withButton) {
        closeModal();
      } else {
        setShowInlineSuccess(true);
      }
    } else {
      setError(resultAction.payload || createLanguage.submitError || "No se pudo crear la agencia.");
    }
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      {(error || createError) ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold leading-6 text-red-700">
          {error || createError}
        </div>
      ) : null}

      <Section
        title={createLanguage.agencySectionLabel || "Agency details"}
        help={createLanguage.agencySectionHelp || "Start with the main information of the agency."}
      >
        <Field className="md:col-span-12" label={createLanguage.agencyNameLabel || "Nombre de la agencia"}>
          <input type="text" name="AgencyName" className={inputClass} value={form.AgencyName} onChange={handleChange} placeholder={createLanguage.agencyNamePlaceholder || "Ej. Agencia Sol Travel"} />
        </Field>
      </Section>

      <Section
        title={createLanguage.contactSectionLabel || "Primary contact"}
        help={createLanguage.contactSectionHelp || "Add the main person we should contact for this agency."}
      >
        <Field className="md:col-span-4" label={createLanguage.contactNameLabel || "Nombre contacto"}>
          <input type="text" name="ContactName" className={inputClass} value={form.ContactName} onChange={handleChange} />
        </Field>
        <Field className="md:col-span-4" label={createLanguage.contactLastNameLabel || "Apellido contacto"}>
          <input type="text" name="ContactLastName" className={inputClass} value={form.ContactLastName} onChange={handleChange} />
        </Field>
        <Field className="md:col-span-4" label={createLanguage.contactTitleLabel || "Puesto"}>
          <input type="text" name="ContactTitle" className={inputClass} value={form.ContactTitle} onChange={handleChange} />
        </Field>
        <Field className="md:col-span-6" label={createLanguage.emailLabel || "Email"}>
          <input type="email" name="Email" className={inputClass} value={form.Email} onChange={handleChange} />
        </Field>
        <Field className="md:col-span-3" label={createLanguage.primaryPhoneLabel || "Teléfono principal"}>
          <input type="text" name="Phone1" className={inputClass} value={form.Phone1} onChange={handleChange} />
        </Field>
        <Field className="md:col-span-3" label={createLanguage.secondaryPhoneLabel || "Teléfono secundario"}>
          <input type="text" name="Phone2" className={inputClass} value={form.Phone2} onChange={handleChange} />
        </Field>
      </Section>

      <Section
        title={createLanguage.locationSectionLabel || "Location"}
        help={createLanguage.locationSectionHelp || "Tell us where the agency is located."}
      >
        <Field className="md:col-span-12" label={createLanguage.addressLabel || "Dirección"}>
          <textarea name="Address" rows="2" className={textareaClass} value={form.Address} onChange={handleChange} placeholder={createLanguage.addressPlaceholder || "Escribe la dirección"} />
        </Field>
        <Field className="md:col-span-12" label={createLanguage.address2Label || "Dirección 2"}>
          <input type="text" name="Address_2" className={inputClass} value={form.Address_2} onChange={handleChange} />
        </Field>
        <Field className="md:col-span-3" label={createLanguage.countryLabel || createLanguage.countryIdLabel || "País"}>
          <select name="Id_Country" className={inputClass} value={form.Id_Country} onChange={handleChange} disabled={loadingCountries}>
            <option value="">
              {loadingCountries ? (createLanguage.loadingCountriesLabel || "Cargando países...") : (createLanguage.selectCountryLabel || "Selecciona un país")}
            </option>
            {countries.map((country) => (
              <option key={country.Id} value={country.Id}>{country.Name}</option>
            ))}
          </select>
        </Field>
        <Field className="md:col-span-3" label={createLanguage.stateLabel || createLanguage.stateIdLabel || "Estado"}>
          <select name="Id_State" className={inputClass} value={form.Id_State} onChange={handleChange} disabled={!form.Id_Country || loadingStates}>
            <option value="">
              {!form.Id_Country
                ? (createLanguage.selectCountryFirstLabel || "Selecciona primero un país")
                : loadingStates
                  ? (createLanguage.loadingStatesLabel || "Cargando estados...")
                  : (createLanguage.selectStateLabel || "Selecciona un estado")}
            </option>
            {states.map((state) => (
              <option key={state.Id} value={state.Id}>{state.Name}</option>
            ))}
          </select>
        </Field>
        <Field className="md:col-span-3" label={createLanguage.cityLabel || "Ciudad"}>
          <input type="text" name="City" className={inputClass} value={form.City} onChange={handleChange} />
        </Field>
        <Field className="md:col-span-3" label={createLanguage.zipCodeLabel || "Código postal"}>
          <input type="text" name="ZipCode" className={inputClass} value={form.ZipCode} onChange={handleChange} />
        </Field>
      </Section>

      <Section
        title={createLanguage.accessSectionLabel || "Access"}
        help={createLanguage.accessSectionHelp || "Create the access password for this agency."}
      >
        <Field className="md:col-span-6" label={createLanguage.passwordLabel || "Password"}>
          <input type="password" name="password" className={inputClass} value={form.password} onChange={handleChange} />
        </Field>
        <Field className="md:col-span-6" label={createLanguage.confirmPasswordLabel || "Confirmar password"}>
          <input type="password" name="passwordv" className={inputClass} value={form.passwordv} onChange={handleChange} />
        </Field>
        <div className="flex items-start gap-3 text-sm font-bold text-slate-600 md:col-span-12">
          <input id="agency-create-terms" type="checkbox" name="terms" checked={form.terms} onChange={handleChange} required className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#f6c833]" />
          <label htmlFor="agency-create-terms" className="m-0">
            {createLanguage.termsTextBefore || "He leído y acepto los términos y condiciones para el uso del "}
            <a href={multimediaTermsLink} target="_blank" rel="noreferrer" className="text-[#4aa6cf] underline hover:text-[#11183f]">
              {createLanguage.termsLinkText || "material multimedia de Marival Resorts"}
            </a>
            {createLanguage.termsTextAfter || "."}
          </label>
        </div>
        <div className="flex items-start gap-3 text-sm font-bold text-slate-600 md:col-span-12">
          <input id="agency-create-policies" type="checkbox" name="policies" checked={form.policies} onChange={handleChange} required className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#f6c833]" />
          <label htmlFor="agency-create-policies" className="m-0">
            {createLanguage.policiesTextBefore || "He leído y acepto los términos del "}
            <a href={policyLinks.privacy} target="_blank" rel="noreferrer" className="text-[#4aa6cf] underline hover:text-[#11183f]">
              {createLanguage.policiesPrivacyLink || "Aviso de Privacidad"}
            </a>
            {createLanguage.policiesTextMiddle || " y la "}
            <a href={policyLinks.cookie} target="_blank" rel="noreferrer" className="text-[#4aa6cf] underline hover:text-[#11183f]">
              {createLanguage.policiesCookieLink || "Política de Cookies"}
            </a>
            {createLanguage.policiesTextAfter || "."}
          </label>
        </div>
      </Section>

      <div className="flex flex-wrap justify-end gap-3 pt-2">
        {withButton ? (
          <button
            type="button"
            className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-5 text-sm font-black text-[#11183f] hover:bg-slate-50 disabled:cursor-wait disabled:opacity-70"
            onClick={closeModal}
            disabled={creating}
          >
            {createLanguage.cancelButton || "Cancelar"}
          </button>
        ) : null}

        <button
          type="submit"
          className="inline-flex h-11 items-center justify-center rounded-xl border-0 bg-[#f6c833] px-6 text-sm font-black text-[#11183f] shadow-[0_12px_24px_rgba(246,200,51,0.22)] hover:bg-[#ffd84d] disabled:cursor-wait disabled:opacity-70"
          disabled={creating}
        >
          {creating ? (createLanguage.submittingButton || "Creando...") : (createLanguage.submitButton || "Guardar / Crear agencia")}
        </button>
      </div>
    </form>
  );

  const renderInlineSuccess = () => (
    <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-marival md:p-10">
      <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-600">
        <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h3 className="m-0 text-3xl font-black leading-tight text-[#11183f]">
        {language.successTitle || "Agencia registrada correctamente"}
      </h3>
      <p className="mx-auto mb-7 mt-4 max-w-2xl text-base leading-7 text-slate-600">
        {language.successText || "La agencia se creó correctamente. Si quieres seguir ahora mismo, puedes registrar un agente para esta agencia."}
      </p>
      <div className="flex flex-col justify-center gap-3 sm:flex-row">
        <NavLink
          to={`/agentregister?lang=${currentLanguage}`}
          className="inline-flex h-12 min-w-[190px] items-center justify-center rounded-xl bg-[#f6c833] px-5 text-sm font-black text-[#11183f] shadow-[0_12px_24px_rgba(246,200,51,0.26)] hover:bg-[#ffd84d]"
        >
          {language.successPrimaryAction || "Registrar agente"}
        </NavLink>
        <button
          type="button"
          className="inline-flex h-12 min-w-[190px] items-center justify-center rounded-xl border border-slate-200 bg-white px-5 text-sm font-black text-[#11183f] hover:bg-slate-50"
          onClick={() => {
            resetForm();
            setShowInlineSuccess(false);
          }}
        >
          {language.successSecondaryAction || "Registrar otra agencia"}
        </button>
      </div>
    </div>
  );

  const modalContent = showModal ? (
    <div className="absolute inset-x-0 top-0 z-[200000] flex min-h-[100dvh] items-start justify-center px-4 py-8 md:px-6" data-agency-create-modal="true">
      <div className="w-full max-w-5xl rounded-2xl border border-slate-200 bg-white shadow-[0_30px_70px_rgba(15,23,42,0.22)]" data-agency-create-modal="true">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h4 className="m-0 text-xl font-black text-[#11183f]">{title}</h4>
          <button
            type="button"
            onClick={closeModal}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-2xl leading-none text-slate-500 hover:bg-slate-50"
            disabled={creating}
          >
            ×
          </button>
        </div>

        <div className="p-5 md:p-6">
          {renderForm()}
        </div>
      </div>
    </div>
  ) : null;

  if (!withButton) {
    if (showInlineSuccess) {
      return renderInlineSuccess();
    }

    return (
      <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200 bg-white p-5 shadow-marival md:p-6">
        {showInlineTitle ? (
          <div className="mb-6">
            <h3 className="m-0 text-2xl font-black text-[#11183f]">{title}</h3>
          </div>
        ) : null}

        {renderForm()}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        className="inline-flex h-11 items-center justify-center rounded-xl border-0 bg-[#f6c833] px-6 text-sm font-black text-[#11183f] shadow-[0_12px_24px_rgba(246,200,51,0.22)] hover:bg-[#ffd84d]"
        onClick={openModal}
      >
        {buttonText || createLanguage.buttonText || "Crear agencia"}
      </button>

      {typeof document !== "undefined" ? createPortal(modalContent, document.body) : null}
    </div>
  );
}

export default AgencyCreate;
